import { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Table } from "antd";
import { Pagination } from "antd";
import { Search, Grid as ColumnsIcon, ChevronDown, X } from "react-feather";
import moment from "moment";
import Loader from "../../../../components/Loader/Loader";
import ActionDots from "../../../../components/Datatable/ActionDots";
import { getCustomerPayments } from "../../../../actions/admin/crm-actions";
import { sendPostRequest } from "../../../../actions/app-actions";
import { capitalizeFirstLetter, notification } from "../../../../utils/utility";
import "antd/dist/antd.css";
import "../customer/customer.scss";
import "./booking-history.scss";

const DEFAULT_VISIBLE = {
  booking_date: true,
  event: true,
  invitee: true,
  venue: true,
  status: true,
  payment: true,
  time: false,
  event_format: false,
  event_type: false,
  invitee_email: false,
  invitee_phone: false,
  staff: false,
  commission: false,
  refund_status: false,
};

const COLUMN_LABELS = {
  booking_date: "Booking date",
  event: "Event",
  invitee: "Invitee",
  venue: "Venue",
  status: "Status",
  payment: "Payment",
  time: "Time",
  event_format: "Event format",
  event_type: "Event type",
  invitee_email: "Invitee email",
  invitee_phone: "Invitee phone",
  staff: "Staff",
  commission: "Commission",
  refund_status: "Refund status",
};

const fullName = (first, last) =>
  [capitalizeFirstLetter(first || ""), capitalizeFirstLetter(last || "")]
    .join(" ")
    .trim();

const formatWhen = (date, time) => {
  const parsed = moment(date, "DD-MM-YYYY", true);
  const label = parsed.isValid() ? parsed.format("MMM D, YYYY") : date || "—";
  return time ? `${label} · ${time}` : label;
};

const formatMoney = (amount, currency) => {
  const value = Number(amount);
  const safe = Number.isNaN(value) ? 0 : value;
  const text = Number.isInteger(safe) ? String(safe) : safe.toFixed(2);
  return currency ? `${text} ${currency}` : text;
};

const eventTypeLabel = (paid) => (paid === "E" ? "Paid" : "Free");

const refundLabel = (status) => {
  if (status === "in-review") return "Refund pending";
  if (status === "approved") return "Refunded";
  if (status === "unapproved") return "Refund declined";
  return "—";
};

const statusClass = (code, label) => {
  const text = (label || "").toLowerCase();
  if (code === "CO" || text.includes("completed")) return "bh-status bh-status--completed";
  if (code === "CC" || code === "CS" || text.includes("cancel")) return "bh-status bh-status--cancelled";
  if (code === "MN" || text.includes("no show")) return "bh-status bh-status--noshow";
  if (code === "P" || text.includes("pending")) return "bh-status bh-status--pending";
  if (code === "R" || text.includes("reject")) return "bh-status bh-status--cancelled";
  return "bh-status bh-status--active";
};

const DetailField = ({ label, value }) => (
  <div className="bh-field">
    <span>{label}</span>
    <strong>{value || "—"}</strong>
  </div>
);

const BookingDetails = ({ record, onClose, onRefund, refunding }) => {
  const [confirmRefund, setConfirmRefund] = useState(false);
  if (!record) return null;
  const invitee = fullName(record.invitee_first_name, record.invitee_last_name);
  const staff = fullName(record.staff_first_name, record.staff_last_name);
  const canRefund = record.payment_refund_status === "in-review";

  return (
    <>
      <div className="crm-profile-backdrop" onClick={onClose} />
      <aside className="crm-profile-panel bh-panel" role="dialog" aria-label="Booking details">
        <div className="crm-profile-panel__header">
          <div>
            <h2 className="bh-panel__title">Booking details</h2>
            <p className="bh-panel__when">{formatWhen(record.booking_date, record.booking_time)}</p>
          </div>
          <button type="button" className="crm-profile-panel__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="crm-profile-panel__body bh-panel__body">
          <section className="bh-section">
            <h3>Event</h3>
            <div className="bh-grid">
              <DetailField label="Event" value={record.event_name} />
              <DetailField label="Format" value={record.event_format} />
              <DetailField label="Event type" value={eventTypeLabel(record.is_event_paid)} />
              <DetailField label="Venue" value={record.venue} />
            </div>
          </section>
          <section className="bh-section">
            <h3>Invitee</h3>
            <div className="bh-grid">
              <DetailField label="Name" value={invitee} />
              <DetailField label="Staff" value={staff} />
              <DetailField label="Email" value={record.invitee_email} />
              <DetailField label="Phone" value={record.invitee_phone} />
            </div>
          </section>
          <section className="bh-section">
            <h3>Payment</h3>
            <div className="bh-grid">
              <DetailField label="Status" value={record.booking_status} />
              <DetailField
                label="Amount charged"
                value={formatMoney(record.booking_total_amount, record.currency)}
              />
              <DetailField
                label="Commission"
                value={formatMoney(record.commission, record.currency)}
              />
              <DetailField label="Refund status" value={refundLabel(record.payment_refund_status)} />
            </div>
          </section>
          <div className="bh-actions">
            {canRefund ? (
              confirmRefund ? (
                <div className="bh-actions__confirm">
                  <span>Refund {formatMoney(record.booking_total_amount, record.currency)}?</span>
                  <button
                    type="button"
                    className="bh-btn bh-btn--ghost"
                    onClick={() => setConfirmRefund(false)}
                    disabled={refunding}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bh-btn bh-btn--primary"
                    onClick={() => onRefund(record.id)}
                    disabled={refunding}
                  >
                    {refunding ? "Refunding…" : "Confirm refund"}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="bh-btn bh-btn--primary"
                  onClick={() => setConfirmRefund(true)}
                >
                  Execute refund
                </button>
              )
            ) : (
              <span className="bh-actions__none">No actions for this booking.</span>
            )}
          </div>
          <p className="bh-note">
            All of the information that used to require horizontal scrolling is still here, attached to the relevant booking.
          </p>
        </div>
      </aside>
    </>
  );
};

const ClientPayments = (props) => {
  const navigate = useNavigate();
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });
  const [searchValue, setSearchValue] = useState("");
  const [eventType, setEventType] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [status, setStatus] = useState("");
  const [visibleCols, setVisibleCols] = useState({ ...DEFAULT_VISIBLE });
  const [showColPicker, setShowColPicker] = useState(false);
  const [selected, setSelected] = useState(null);
  const [refunding, setRefunding] = useState(false);
  const [sort, setSort] = useState({ field: "id", order: "desc" });
  const colPickerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const fetchTableData = useCallback((params = {}) => {
    const nextPagination = params.pagination || pagination;
    const nextSearch = params.search !== undefined ? params.search : searchValue;
    const nextType = params.eventType !== undefined ? params.eventType : eventType;
    const nextDate = params.datePreset !== undefined ? params.datePreset : datePreset;
    const nextStatus = params.status !== undefined ? params.status : status;
    const nextSort = params.sort || sort;
    const filter = {};
    if (nextSearch) filter.search = nextSearch;
    if (nextType) filter.is_event_paid = nextType;
    if (nextDate && nextDate !== "all") filter.date_preset = nextDate;
    if (nextStatus) filter.status = nextStatus;

    setIsDataLoading(true);
    props.getCustomerPayments({
      data: {
        pageSize: nextPagination.pageSize,
        pageNumber: nextPagination.current,
        sortField: nextSort.field,
        sortOrder: nextSort.order,
        filter,
      },
      onSuccess: (response) => {
        setData((response && response.entity) || []);
        setPagination({
          ...nextPagination,
          total: response && response.totalCount ? response.totalCount : 0,
        });
        setIsDataLoading(false);
      },
      onError: () => {
        setData([]);
        setIsDataLoading(false);
      },
    });
  }, [pagination, searchValue, eventType, datePreset, status, sort]);

  useEffect(() => {
    fetchTableData({ pagination: { current: 1, pageSize: 12, total: 0 } });
    const handleOutside = (event) => {
      if (colPickerRef.current && !colPickerRef.current.contains(event.target)) {
        setShowColPicker(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchTableData({
        pagination: { ...pagination, current: 1 },
        search: value,
      });
    }, 350);
  };

  const changeFilter = (patch) => {
    const next = {
      eventType,
      datePreset,
      status,
      ...patch,
    };
    if (patch.eventType !== undefined) setEventType(patch.eventType);
    if (patch.datePreset !== undefined) setDatePreset(patch.datePreset);
    if (patch.status !== undefined) setStatus(patch.status);
    fetchTableData({
      pagination: { ...pagination, current: 1 },
      ...next,
    });
  };

  const openDetails = (record) => setSelected(record);

  const refundHandler = (bookingId) => {
    setRefunding(true);
    props.sendPostRequest({
      url: "/booking/refund",
      data: { booking_id: bookingId },
      onSuccess: (response) => {
        setRefunding(false);
        const succeeded = response && (response.success === true || response.success === "true");
        if (succeeded) {
          notification.success(response.message || "Refund completed.");
          setSelected(null);
          fetchTableData({ pagination });
        } else {
          notification.error((response && response.message) || "Could not refund this booking.");
        }
      },
      onError: () => {
        setRefunding(false);
        notification.error("Could not refund this booking. Please try again.");
      },
    });
  };

  const exportRows = () => {
    const headers = [
      "Booking date",
      "Time",
      "Event",
      "Event format",
      "Event type",
      "Venue",
      "Invitee",
      "Invitee email",
      "Invitee phone",
      "Staff",
      "Status",
      "Amount charged",
      "Currency",
      "Commission",
      "Refund status",
    ];
    const lines = data.map((row) => [
      row.booking_date,
      row.booking_time,
      row.event_name,
      row.event_format,
      eventTypeLabel(row.is_event_paid),
      row.venue,
      fullName(row.invitee_first_name, row.invitee_last_name),
      row.invitee_email,
      row.invitee_phone,
      fullName(row.staff_first_name, row.staff_last_name),
      row.booking_status,
      row.booking_total_amount,
      row.currency,
      row.commission,
      refundLabel(row.payment_refund_status),
    ]);
    const csv = [headers, ...lines]
      .map((line) =>
        line
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "booking-history.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const onlyDefaults =
    Object.keys(DEFAULT_VISIBLE).every((key) => visibleCols[key] === DEFAULT_VISIBLE[key]);

  const allColumns = [
    {
      key: "booking_date",
      title: "Booking date",
      dataIndex: "booking_date",
      sorter: true,
      render: (_, row) => (
        <span className="bh-date">{formatWhen(row.booking_date, row.booking_time)}</span>
      ),
    },
    {
      key: "event",
      title: "Event",
      dataIndex: "event_name",
      render: (_, row) => (
        <div className="bh-stack">
          <strong>{row.event_name || "—"}</strong>
          <span>
            {[row.event_format, eventTypeLabel(row.is_event_paid)].filter(Boolean).join(" · ")}
          </span>
        </div>
      ),
    },
    {
      key: "invitee",
      title: "Invitee",
      dataIndex: "invitee_first_name",
      render: (_, row) => (
        <div className="bh-stack">
          <strong>{fullName(row.invitee_first_name, row.invitee_last_name) || "—"}</strong>
          {row.invitee_email ? <span>{row.invitee_email}</span> : null}
        </div>
      ),
    },
    {
      key: "venue",
      title: "Venue",
      dataIndex: "venue",
      render: (value) => <span className="bh-venue">{value || "—"}</span>,
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "booking_status",
      sorter: true,
      render: (value, row) => (
        <span className={statusClass(row.booking_status_code, value)}>{value || "—"}</span>
      ),
    },
    {
      key: "payment",
      title: "Payment",
      dataIndex: "booking_total_amount",
      render: (value, row) => (
        <span className="bh-pay">{formatMoney(value, row.currency)}</span>
      ),
    },
    {
      key: "time",
      title: "Time",
      dataIndex: "booking_time",
      render: (value) => value || "—",
    },
    {
      key: "event_format",
      title: "Event format",
      dataIndex: "event_format",
      render: (value) => value || "—",
    },
    {
      key: "event_type",
      title: "Event type",
      dataIndex: "is_event_paid",
      render: (value) => eventTypeLabel(value),
    },
    {
      key: "invitee_email",
      title: "Invitee email",
      dataIndex: "invitee_email",
      render: (value) => value || "—",
    },
    {
      key: "invitee_phone",
      title: "Invitee phone",
      dataIndex: "invitee_phone",
      render: (value) => value || "—",
    },
    {
      key: "staff",
      title: "Staff",
      dataIndex: "staff_first_name",
      render: (_, row) => fullName(row.staff_first_name, row.staff_last_name) || "—",
    },
    {
      key: "commission",
      title: "Commission",
      dataIndex: "commission",
      render: (value, row) => formatMoney(value, row.currency),
    },
    {
      key: "refund_status",
      title: "Refund status",
      dataIndex: "payment_refund_status",
      render: (value) => refundLabel(value),
    },
    {
      key: "actions",
      title: "",
      dataIndex: "actions",
      always: true,
      width: 56,
      render: (_, row) => (
        <button
          type="button"
          className="bh-dots"
          aria-label="Open booking details"
          onClick={(event) => {
            event.stopPropagation();
            openDetails(row);
          }}
        >
          <ActionDots />
        </button>
      ),
    },
  ];

  const visibleColumns = allColumns.filter((column) => column.always || visibleCols[column.key]);

  return (
    <div className="bh-page">
      <Loader isShowLoader={isDataLoading} />

      <div className="crm-page-header">
        <div className="crm-page-header__left">
          <span className="bh-crumb">CRM / Booking History</span>
          <h1 className="crm-page-header__title">Booking History</h1>
          <p className="crm-page-header__sub">Review payments and past bookings.</p>
          <p className="bh-count">
            {pagination.total || 0} booking{pagination.total === 1 ? "" : "s"}
            <span> · Click a row for the full record</span>
          </p>
        </div>
        <div className="crm-page-header__actions">
          <button type="button" className="bh-btn bh-btn--ghost" onClick={exportRows}>
            Export
          </button>
          <button
            type="button"
            className="bh-btn bh-btn--primary"
            onClick={() => navigate("/admin/events/list")}
          >
            New booking
          </button>
        </div>
      </div>

      <div className="crm-toolbar-card">
        <div className="crm-toolbar bh-toolbar">
          <label className="crm-search-box">
            <Search size={15} className="crm-search-box__icon" aria-hidden="true" />
            <input
              type="search"
              className="crm-search-box__input"
              placeholder="Search invitee, event or email"
              value={searchValue}
              onChange={handleSearchChange}
              autoComplete="off"
            />
          </label>
          <div className="bh-filters">
            <select
              className="bh-select"
              value={eventType}
              onChange={(event) => changeFilter({ eventType: event.target.value })}
              aria-label="Event type"
            >
              <option value="">All event types</option>
              <option value="E">Paid</option>
              <option value="D">Free</option>
            </select>
            <select
              className="bh-select"
              value={datePreset}
              onChange={(event) => changeFilter({ datePreset: event.target.value })}
              aria-label="Date range"
            >
              <option value="all">All dates</option>
              <option value="today">Today</option>
              <option value="last7">Last 7 days</option>
              <option value="last30">Last 30 days</option>
              <option value="month">This month</option>
            </select>
            <select
              className="bh-select"
              value={status}
              onChange={(event) => changeFilter({ status: event.target.value })}
              aria-label="Status"
            >
              <option value="">All statuses</option>
              <option value="A">Active</option>
              <option value="C">Confirm</option>
              <option value="R">Reject</option>
              <option value="CC">Cancel by client</option>
              <option value="CS">Cancel by service provider</option>
              <option value="CO">Completed</option>
              <option value="MN">Mark as no-show</option>
              <option value="TEMP">Temp</option>
            </select>
          </div>

          <div className="crm-toolbar__right">
            <span className="bh-core-note">
              {onlyDefaults ? "Core columns only" : "Custom columns"}
              <span> · all details stay available</span>
            </span>
            <div className="crm-col-picker-wrap" ref={colPickerRef}>
              <button
                type="button"
                className={`crm-toolbar-btn${showColPicker ? " crm-toolbar-btn--active" : ""}`}
                onClick={() => setShowColPicker((open) => !open)}
              >
                <ColumnsIcon size={14} /> Columns <ChevronDown size={12} />
              </button>
              {showColPicker ? (
                <div className="crm-col-picker">
                  <p className="crm-col-picker__title">Show / hide columns</p>
                  {Object.keys(DEFAULT_VISIBLE).map((key) => (
                    <label key={key} className="crm-col-picker__item">
                      <input
                        type="checkbox"
                        checked={!!visibleCols[key]}
                        onChange={() =>
                          setVisibleCols((current) => ({ ...current, [key]: !current[key] }))
                        }
                      />
                      {COLUMN_LABELS[key]}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="crm-content-card">
        <Table
          className="crm_customer_table bh-table"
          columns={visibleColumns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={false}
          loading={isDataLoading}
          onChange={(_pagination, _filters, sorter) => {
            const nextSort = {
              field: sorter.field || "id",
              order: sorter.order === "ascend" ? "asc" : "desc",
            };
            setSort(nextSort);
            fetchTableData({
              pagination: { ...pagination, current: 1 },
              sort: nextSort,
            });
          }}
          onRow={(record) => ({
            onClick: () => openDetails(record),
            className: selected && selected.id === record.id ? "crm-selected-row" : "",
          })}
          locale={{
            emptyText: (
              <div className="crm-empty-state">
                <p className="crm-empty-state__text">No bookings match these filters.</p>
              </div>
            ),
          }}
        />
        <div className="bh-footer">
          <span>
            Showing {data.length} of {pagination.total || 0} bookings
          </span>
          <Pagination
            className="list-view-pagination"
            current={pagination.current}
            total={pagination.total}
            pageSize={pagination.pageSize}
            showSizeChanger={false}
            onChange={(page) =>
              fetchTableData({ pagination: { ...pagination, current: page } })
            }
          />
        </div>
      </div>

      {selected ? (
        <BookingDetails
          record={selected}
          onClose={() => setSelected(null)}
          onRefund={refundHandler}
          refunding={refunding}
        />
      ) : null}
    </div>
  );
};

export default connect(null, {
  getCustomerPayments,
  sendPostRequest,
})(ClientPayments);
